import React, { useCallback, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { defaultTabId, tabIdForPath, goToTab } from "./pathTabs"

// The active tab of a listing page, read from and written to the path (see Layout/routes.js).
export function usePathTab(route) {
    const location = useLocation()
    const navigate = useNavigate()
    const tabIdInPath = tabIdForPath(route, location.pathname)
    const fallbackTabId = defaultTabId(route)

    // An unknown slug settles on the route path (the default tab), keeping a shared link's filters.
    useEffect(() => {
        if (tabIdInPath === undefined) goToTab(navigate, location, route, fallbackTabId, { replace: true, clearFilters: false })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIdInPath, location.pathname])

    const activeTabId = tabIdInPath ?? fallbackTabId

    // ListingTabs calls onChange(id, tab); a tab object carries no keepFilters, so filters drop.
    // Re-clicking the already-active tab is a no-op: nothing to switch to, so don't drop
    // ?filter= or push a duplicate history entry for the tab the user is already on.
    const changeTab = useCallback(
        (tabId, { keepFilters = false } = {}) => {
            if (tabId === activeTabId) return
            goToTab(navigate, location, route, tabId, { clearFilters: !keepFilters })
        },
        [navigate, location, route, activeTabId]
    )

    return { activeTabId, changeTab }
}

// Class components can't call hooks: this passes activeTabId and changeTab as props.
export const withPathTab = (route) => (WrappedComponent) => {
    function WithPathTab(props) {
        const { activeTabId, changeTab } = usePathTab(route)
        return <WrappedComponent {...props} activeTabId={activeTabId} changeTab={changeTab} />
    }
    WithPathTab.displayName = `withPathTab(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`
    return WithPathTab
}
